import React, { useEffect, useRef, useState } from "react"
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, IconButton, Skeleton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { RmDashboardType, filterType } from "../../../redux-store/types/api-types";
import CircleIcon from '@mui/icons-material/Circle';
import CustomDropdown from "../../../lib/formik/CustomDropdown";
import { calculateOthersInPieChart, formatCurrency, toINR } from "../../../utils/utilityFunctions";
import { table } from "console";
import { BlueRightIcon } from "../../../assets/SvgIcons";
import ListModel from "./ListModel";
import { RootStateType } from "../../../redux-store/reducers";
import { useSelector } from "react-redux";
import { getChartA11yOptions } from "../../../utils/highchartsAccessibility";

interface Props {
    labelData: filterType[];
    data: Array<Array<string | number>>;
    totalValue: filterType;
    isLoading: boolean;
    showSlider?: boolean
    align?: 'center' | 'end' | 'start' | 'left' | 'right',
    defaultValue?: filterType,
    title?: string;
    colorArr?: string[];
    labelOrderEnabled?: boolean;
    showOriginalTotalValue?: boolean;
}

interface defaultTotal {
    title: string,
    value: number,
    percent?: number
}

const pieChartColor = ["#00A76F","#7A6A04", "#3C92DC ", "#3CA9DC", "#FF5630", "#0D3C6B"]

export default function CustomePieChart(props: Props): JSX.Element {
    const theme = useTheme();
    const xllaptopView = useMediaQuery(theme.breakpoints.down("xl"))
    const laptopView = useMediaQuery(theme.breakpoints.down("lg"))
    const tableView = useMediaQuery(theme.breakpoints.down("md"))
    const mobileView = useMediaQuery(theme.breakpoints.down("sm"))

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const modifiedData = calculateOthersInPieChart({ labelData: props.labelData, totalValue: props.totalValue, data: props.data, labelOrderEnabled: props?.labelOrderEnabled })

    // Labels excluded from click/drill-down behaviour. Added the new invoice
    // statuses ('Not Generated', 'Non Billable', 'Others') so they behave like
    // the existing 'Freezed'/'Unfreezed' segments and are not treated as drillable categories.
    const avoidedLabels = ['Issuer', 'Issuers', 'Sectors', 'Sector', 'Instruments', 'Instrument', 'Company', 'Companies', 'Scheme', 'Schemes', 'Freezed', 'Unfreezed', 'Not Generated', 'Non Billable', 'Others', 'Total']

    const [subtit, setSubTit] = useState("all");
    const [open, setOpen] = useState(false);
    const [centerTitle, setCenterTitle] = useState<defaultTotal>({
        title: props?.totalValue?.label || "",
        value: Number(props?.totalValue?.value) || 0
    })

    // Refs keep the Highcharts point/series event callbacks in sync with the
    // latest data. The callbacks are registered once via `plotOptions` and
    // would otherwise close over the initial render's `modifiedData` / total.
    const hoverDataRef = useRef({
        labelData: modifiedData?.labelData as filterType[] | undefined,
        totalValue: props?.totalValue,
    });
    useEffect(() => {
        hoverDataRef.current = {
            labelData: modifiedData?.labelData as filterType[] | undefined,
            totalValue: props?.totalValue,
        };
    }, [modifiedData, props?.totalValue]);

    const resetCenterToTotal = () => {
        setSubTit("all");
        setCenterTitle({
            title: hoverDataRef.current?.totalValue?.label || '',
            value: Number(hoverDataRef.current?.totalValue?.value) || 0,
        });
    };

    const applyHoverCenter = (name: string, y: number) => {
        const labelMatch = hoverDataRef.current?.labelData?.find(
            (l: filterType) => l?.label === name
        );
        const totalNum = Number(hoverDataRef.current?.totalValue?.value) || 0;
        let percent: number | undefined;
        if (labelMatch && !Number.isNaN(Number(labelMatch.value))) {
            percent = Number(labelMatch.value);
        } else if (totalNum > 0 && typeof y === 'number') {
            percent = (y / totalNum) * 100;
        }
        setSubTit(name);
        setCenterTitle((pre) => (
            pre?.title === name && pre?.value === y && pre?.percent === percent
                ? pre
                : { title: name, value: y, percent }
        ));
    };
    const pieChartA11yLabel = props?.title || props?.totalValue?.label || 'Distribution';
    const pieA11y = getChartA11yOptions({
        altText: `${pieChartA11yLabel} pie chart`,
        description: `Donut chart visualizing the breakdown of ${pieChartA11yLabel} across categories.`,
        pointDescriptionFormatter: (point: any) => {
            const name = String(point?.name ?? '').trim() || 'Segment';
            const percentage = Number(point?.percentage);
            if (Number.isNaN(percentage)) return name;
            return `${name}, ${percentage.toFixed(2)} percent`;
        },
    });

    // Builds the HTML shown inside the donut. When `percent` is provided
    // (i.e. a slice is being hovered/focused) the third line renders that
    // percentage instead of the static "(Approx.)" caption.
    const buildCenterHTML = (center: defaultTotal): string => {
        const titleSize = mobileView ? 12 : tableView ? 14 : 16;
        const valueSize = mobileView ? 18 : tableView ? 20 : 22;
        const captionSize = mobileView ? 10 : tableView ? 12 : 14;
        const isAvoided = avoidedLabels.includes(center?.title);
        const hasValue = (center?.value ?? 0) > 0;
        const valueText = hasValue
            ? (isAvoided ? `${center.value}` : formatCurrency({ value: center.value, locale, currencyCode }))
            : '0';
        const hasPercent = typeof center?.percent === 'number' && Number.isFinite(center.percent);
        const captionText = hasPercent
            ? `${(center.percent as number).toFixed(2)}%`
            : (hasValue ? (isAvoided ? '' : '(Approx.)') : '0');

        return `
            <div style="text-align: center; padding: 22px;">
                <span style="font-size: ${titleSize}px; color: ${theme.palette.primary[500]}; font-weight: 500; opacity: 0.5">${center?.title || 'Loading...'}</span>
                <br>
                <span style="font-size: ${valueSize}px; color: ${theme.palette.primary[500]}; font-weight: 500;">${valueText}</span>
                <br>
                <span style="font-size: ${captionSize}px; color: ${theme.palette.primary[500]}; font-weight: 500;">${captionText}</span>
            </div>`;
    };
    const [options, setOptions] = useState<Highcharts.Options>({
        title: pieA11y.title as Highcharts.TitleOptions,
        accessibility: pieA11y.accessibility as Highcharts.AccessibilityOptions,
        credits: {
            enabled: false,
        },
        subtitle: {
            useHTML: true,
            text: buildCenterHTML(centerTitle),
            verticalAlign: "middle",
            y: 30,
            style: {
                fontSize: "24px",
            }
        },
        plotOptions: {
            series: {
                dataLabels: {
                    enabled: false,
                    style: {}
                },
                // Series-level mouseOut fires when the pointer leaves the whole
                // pie (i.e. is no longer over any slice). Reset the center
                // back to the total so the donut returns to its default state.
                events: {
                    mouseOut: function () {
                        resetCenterToTotal();
                    },
                },
                point: {
                    // Point-level mouseOver covers both mouse hover and keyboard
                    // focus (the a11y module proxies keyboard navigation through
                    // the same event). This updates the center to show the
                    // hovered slice's label, value and percentage.
                    events: {
                        mouseOver: function (this: any) {
                            applyHoverCenter(this.name, this.y);
                        },
                    },
                },
            },
        },
        tooltip: {
            // The native tooltip stays hidden — the donut center IS the tooltip.
            enabled: false,
        },
        // colors: pieChartColor,
        colors: props?.colorArr || ["#00A76F", "#7A6A04", "#3C92DC ", "#3CA9DC", "#FF5630", "#0D3C6B"],
        series: [
            {
                type: "pie",
                name: "Energy production",
                innerSize: "90%",
                size: "83%",
                animation: false,
                // data: [
                //     ["Total", 20.0231569],
                //     ["UK", 15.135],
                //     ["US", 3.8491569],
                //     ["Germany", 3.8491569],
                //     ["France", 3.8491569],
                // ],
                data: modifiedData?.data?.length < 5 ? modifiedData?.data : modifiedData?.data?.slice(0, 5),

            },
        ],
        chart: {
            style: {
                fontFamily: `${theme.typography.fontFamily}`,
            },
            backgroundColor: "transparent",
            polar: true,
            type: 'line',
            // description: pieA11y.chartDescription,
        }
    })

    useEffect(() => {
        setCenterTitle(props.defaultValue as defaultTotal);
    }, [props.defaultValue])

    useEffect(() => {
        setOptions((pre: any) => {
            return {
                ...pre,
                title: pieA11y.title as Highcharts.TitleOptions,
                accessibility: pieA11y.accessibility as Highcharts.AccessibilityOptions,
                subtitle: {
                    ...pre.subtitle,
                    text: buildCenterHTML(centerTitle),
                },
                series: [
                    {
                        type: "pie",
                        name: "Energy production",
                        innerSize: "90%",
                        size: "83%",
                        animation: false,
                        // data: props?.data?.length < 6 ? [...props.data] : [...calculateOthersInPieChart({ labelData: props.labelData, totalValue: props.totalValue, data: props.data })?.data?.slice(0, 5)],
                        data: modifiedData?.data?.length < 5 ? [...modifiedData?.data] : modifiedData?.data?.slice(0, 5),
                    },
                ],
            }
        })
    }, [props.data, centerTitle])

    // When `totalValue` (or its label) changes externally — e.g. the user
    // switches the "By Product" dropdown — sync the center back to the new
    // default rather than leaving a stale hovered slice in the middle.
    // `resetCenterToTotal` is intentionally not in the deps array: it only
    // reads from `hoverDataRef` and is recreated every render, which would
    // cause this effect to fire on every render instead of only on changes
    // to `totalValue`.
    useEffect(() => {
        resetCenterToTotal();
    }, [props?.totalValue?.label, props?.totalValue?.value]);
    return (

        <>

            <Box component={'div'} sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: props.align ? props.align : { xs: 'center', lg: 'space-between' },

                alignItems: 'center',
                width: '100%',
                flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap', lg: 'nowrap', xl: 'nowrap', },
            }}>
                {!props.isLoading && props?.data?.length > 0 ?
                    <HighchartsReact
                        showLoading={true}
                        highcharts={Highcharts}
                        options={options}
                        allowChartUpdate={true}
                        containerProps={{
                            style: {
                                height: mobileView ? 200 : tableView ? 250 : laptopView ? 280 : 300,
                                width: '100%',
                                maxWidth: mobileView ? '100%' : tableView ? '100%' : laptopView ? '100%' : '270px',
                            }
                        }}
                        background="transparent" />
                    :
                    <ChartSkeletonLoader />
                }


                {props.showSlider &&
                    <Box component={'div'} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { lg: '20px', md: '15px', sm: '10px', xs: '10px' },
                        width: '100%',
                        maxWidth: '300px',
                    }}>
                        {
                            !props.isLoading ?
                                props?.labelData?.length < 6 ?
                                    props.labelData?.map((data, index) => (
                                        <Box key={index} style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 15
                                        }}>
                                            <CircleIcon sx={{ color: pieChartColor[index], width: '11px', height: '11px', marginTop: { lg: '8px', sm: '4px', xs: '4px' } }} />
                                            <Box component={'div'} style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', gap: '15px' }}>

                                                <Typography style={{ textWrap: 'wrap', color: 'text.primary', maxWidth: '140px' }} variant="subtitle_400" >{data.label}</Typography>
                                                <Typography style={{ textWrap: 'nowrap', color: 'text.primary' }} variant="subtitle_400" align="right">{(Number(data?.value))?.toFixed(2)} %</Typography>
                                            </Box>
                                        </Box>
                                    )) : props?.labelData?.slice(0, 6)?.map((data, index) => (
                                        <Box key={index} style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 15
                                        }}>

                                            <CircleIcon sx={{
                                                color: 5 === index ? "transparent" : pieChartColor[index],
                                                width: '11px', height: '11px', marginTop: { lg: '8px', sm: '4px', xs: '4px' }
                                            }} />
                                            <Box component={'div'} style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', gap: '15px' }}>

                                                <Typography style={{ textWrap: 'wrap', color: 'text.primary', maxWidth: '140px' }} variant="subtitle_400" >{5 === index ? 'Show more' : data.label}</Typography>
                                                {5 === index ?
                                                    <IconButton onClick={() => setOpen(true)} aria-hidden="true" aria-label="show-more">
                                                        <BlueRightIcon />
                                                    </IconButton> :

                                                    <Typography style={{ textWrap: 'nowrap', color: 'text.primary' }} variant="subtitle_400" align="right">{(Number(data?.value))?.toFixed(2)} %</Typography>
                                                }
                                            </Box>
                                        </Box>
                                    ))

                                :

                                <ChartListSkeletonLoader />}

                    </Box>
                }

            </Box>
            <ListModel open={open} handleClose={() => setOpen(false)} labelData={props?.labelData} title={props.title} />
        </>
    )
}


export const ChartSkeletonLoader = () => {
    return (
        <>
            <Skeleton variant="circular" height={200} style={{
                margin: 35,
                width: '100%',
                maxWidth: '200px',
            }} />
        </>
    )
}

export const ChartListSkeletonLoader = () => {
    return (
        <>
            {/* {Array(5).map((val, i) => ( */}
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            {/* ))} */}
        </>
    )
}


export const FilterSkeleton = () => {
    return (
        // <>
        <Skeleton animation={'wave'} width={200} height={30} />
        // </>
    )
}